// We use a context for keycloak so the components can react when keycloak changes.
// We also have a keycloakService which is global and allows it to be accessed outside of functional components.
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import config from '@/config';
import {
  KeycloakServerLoginTokenResponse,
  consumeOriginalPath,
  consumeResultVerifier,
  getLoginError,
  getServerLoginCallbackResult,
  isInvalidStateLoginError,
  isServerLoginCallback,
  prepareServerLoginRedirect,
  toKeycloakInitOptions,
} from '@/features/Keycloak/keycloakServerLoginFlow';
import {
  fetchCurrentUser,
  logoutAuthenticatedUser,
  setAuthExpiredHandler,
  setAuthTransport,
  setKeycloakInstance,
} from '@/features/Keycloak/keycloakService';
import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js';
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface KeycloakContextProps {
  keycloak: Keycloak | null;
  authenticated: boolean | null;
  logout: () => Promise<void>;
}

const KeycloakContext = createContext<KeycloakContextProps | undefined>(
  undefined,
);

interface KeycloakProviderProps {
  children: React.ReactNode;
}

const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
  const isRun = useRef<boolean>(false);
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  // We want a separate state because keycloak.authenticated has false as initial state. So the UI always flashes
  // the login form. This way we can show a loading spinner instead.
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const createKeycloakInstance = useCallback((): Keycloak => {
    const keycloakConfig: KeycloakConfig = {
      url: config.keyCloak.url,
      realm: config.keyCloak.realm,
      clientId: config.keyCloak.clientId,
    };
    const keycloakInstance: Keycloak = new Keycloak(keycloakConfig);

    setKeycloak(keycloakInstance);
    setKeycloakInstance(keycloakInstance);

    return keycloakInstance;
  }, []);

  const redirectToServerLogin = useCallback(() => {
    void prepareServerLoginRedirect({
      restUrl: config.rest.url,
      realm: config.keyCloak.realm,
      location: window.location,
      storage: window.sessionStorage,
    }).then((loginUrl) => window.location.assign(loginUrl));
  }, []);

  const handleAuthExpired = useCallback((): void => {
    setKeycloak(null);
    setAuthError(null);
    setAuthenticated(false);
    redirectToServerLogin();
  }, [redirectToServerLogin]);

  const logout = useCallback(async (): Promise<void> => {
    setIsLoggingOut(true);
    const logoutResult = await logoutAuthenticatedUser(config.rest.url);
    setKeycloak(null);
    setAuthError(null);

    if (logoutResult === 'keycloak') {
      return;
    }

    setIsLoggingOut(false);
    setAuthenticated(false);
    redirectToServerLogin();
  }, [redirectToServerLogin]);

  const initializeBearerAuth = useCallback(
    async (initOptions: KeycloakInitOptions): Promise<boolean> => {
      const keycloakInstance = createKeycloakInstance();
      setAuthTransport('bearer');

      const _authenticated = await keycloakInstance.init(initOptions);

      if (_authenticated) {
        await fetchCurrentUser(config.rest.url);
      }

      return _authenticated;
    },
    [createKeycloakInstance],
  );

  const completeJwtLogin = useCallback(
    async (loginResult: string): Promise<boolean> => {
      const response = await fetch(`${config.rest.url}/auth/keycloak/result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          loginResult,
          resultVerifier: consumeResultVerifier(window.sessionStorage),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to redeem login result: ${response.status}`);
      }

      const tokenResponse =
        (await response.json()) as KeycloakServerLoginTokenResponse;
      return await initializeBearerAuth(toKeycloakInitOptions(tokenResponse));
    },
    [initializeBearerAuth],
  );

  const tryRestoreSession = useCallback(async (): Promise<boolean> => {
    setAuthTransport('cookie');

    try {
      await fetchCurrentUser(config.rest.url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const completeSessionLogin = useCallback(
    async (): Promise<boolean> => tryRestoreSession(),
    [tryRestoreSession],
  );

  const runServerLoginFlow = useCallback(async () => {
    if (!isServerLoginCallback(window.location)) {
      const restoredAuth =
        config.keyCloak.serverLoginFlow === 'session'
          ? await tryRestoreSession()
          : false;

      if (restoredAuth) {
        setAuthenticated(true);
        return;
      }

      redirectToServerLogin();
      return;
    }

    const loginError = getLoginError(window.location.search);
    if (loginError) {
      if (isInvalidStateLoginError(window.location.search)) {
        redirectToServerLogin();
        return;
      }

      setAuthError(`Authentication failed: ${loginError}.`);
      setAuthenticated(false);
      return;
    }

    const callbackResult = getServerLoginCallbackResult(
      window.location.search,
      config.keyCloak.serverLoginFlow,
    );
    let _authenticated: boolean;
    if (callbackResult.kind === 'jwt') {
      _authenticated = await completeJwtLogin(callbackResult.loginResult);
    } else if (callbackResult.kind === 'session') {
      _authenticated = await completeSessionLogin();
    } else {
      setAuthError(`Missing ${config.keyCloak.serverLoginFlow} login result.`);
      setAuthenticated(false);
      return;
    }

    if (_authenticated) {
      window.history.replaceState(
        null,
        '',
        consumeOriginalPath(window.sessionStorage),
      );
    }

    setAuthenticated(_authenticated);
  }, [
    completeSessionLogin,
    completeJwtLogin,
    redirectToServerLogin,
    tryRestoreSession,
  ]);

  useEffect(() => {
    const run = async () => {
      // We need this check because in StrictMode, all components are mounted twice.
      if (isRun.current) {
        return;
      }

      isRun.current = true;

      try {
        await runServerLoginFlow();
      } catch (error) {
        console.error('Keycloak initialization failed:', error);
        setAuthError('Authentication failed.');
        setAuthenticated(false);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    run();
  }, [runServerLoginFlow]);

  useEffect(() => {
    setAuthExpiredHandler(handleAuthExpired);

    return () => {
      setAuthExpiredHandler(undefined);
    };
  }, [handleAuthExpired]);

  return (
    <KeycloakContext.Provider value={{ keycloak, authenticated, logout }}>
      {authenticated === null || isLoggingOut ? (
        <div className="flex h-[100vh] items-center justify-center">
          <Spinner />
        </div>
      ) : authenticated ? (
        children
      ) : (
        <div className="flex h-[100vh] flex-col items-center justify-center gap-4">
          <div>{authError ?? 'Not authenticated'}</div>
          <Button onClick={redirectToServerLogin}>Retry login</Button>
        </div>
      )}
    </KeycloakContext.Provider>
  );
};

export { KeycloakContext, KeycloakProvider };
